import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { isMutualCandidate, LikeDecisionSchema } from '../like-decision/like-decision';

/**
 * One row of "who liked me" or "who I liked".
 *
 * **`userId` is the other member, and it is the only fact about them here.** The
 * lists are id-and-timestamp lists; a client draws each card by asking the
 * visibility resolver what this viewer may see (N18), so no member-to-member
 * answer data ever travels through the matching module.
 *
 * **`isMutual` is derived server-side, per row.** The client must not compute it
 * by intersecting two paged lists — page 2 of one and page 1 of the other cannot
 * answer the question, and a member being told "it's mutual" incorrectly is the
 * worst false positive this product has.
 *
 * `decision` is on the row rather than implied by the endpoint because the
 * *sent* list shows both: a member reviewing who they passed on is a real
 * screen, and the same shape serves it.
 */
export const LikeSummarySchema = z
  .object({
    userId: UuidSchema,
    decision: LikeDecisionSchema,
    decidedAt: z.iso.datetime(),
    isMutual: z.boolean(),
  })
  .strict()
  .refine((row) => !row.isMutual || isMutualCandidate(row.decision), {
    message: 'Mutuality is a property of two likes; a pass can never be mutual.',
    path: ['isMutual'],
  });

export type LikeSummary = z.infer<typeof LikeSummarySchema>;
